"use client";

import React, { HTMLAttributes } from "react";
import ReactMarkdown from "react-markdown";
import { Text } from "./Text";
import { Anchor } from "./Anchor";
import { H1 } from "./H1";
import { H2 } from "./H2";
import { H3 } from "./H3";
import { H4 } from "./H4";
import { StrongText } from "./StrongText";
import { LI, OL, UL } from "./List";
import rehypeRaw from "rehype-raw";
import cx from "clsx";
import { cleanHtmlRestProps, getImageSizeFromUrl } from "@/lib/utils";
import Image from "next/image";
import { Blockquote } from "./Blockquote";
import { Code } from "./Code";
import { SmallText } from "./SmallText";
import { Table, TBody, TD, TH, THead, TRow } from "./Table";

interface MarkdownRendererProps {
  content: string;
  className?: string;
}

const Div = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div className={cx("font-sans text-body", className)} {...cleanHtmlRestProps(props)} />
);

export const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({
  content,
  className,
}) => {
  return (
    <div className={cx("*:mb-2", className)}>
    <ReactMarkdown
      rehypePlugins={[rehypeRaw]}
      components={{
        p: Text,
        a: ({ href, ...props }) => <Anchor href={href || ""} {...props} />,
        h1: H1,
        h2: H2,
        h3: H3,
        h4: H4,
        b: StrongText,
        ul: UL,
        ol: OL,
        li: LI,
        code: Code,
        small: SmallText,
        strong: StrongText,
        div: Div,
        table: Table,
        td: TD,
        th: TH,
        tr: TRow,
        thead: THead,
        tbody: TBody,
        blockquote: Blockquote,
        img: ({
          width: propsWidth,
          height: propsHeight,
          alt,
          src,
          ...restProps
        }) => {
          const srcStr = typeof src === "string" ? src : "";
          const sizeFromUrl = getImageSizeFromUrl(srcStr);
          const heightRaw = propsHeight || sizeFromUrl?.height || 100;
          const widthRaw = propsWidth || sizeFromUrl?.width || 100;
          const height = Math.max(1, typeof heightRaw === "string" ? parseInt(heightRaw, 10) || 100 : Number(heightRaw) || 100);
          const width = Math.max(1, typeof widthRaw === "string" ? parseInt(widthRaw, 10) || 100 : Number(widthRaw) || 100);
          return (
            <div className="relative min-h-24 w-auto text-start max-w-full inline-block md:me-8 my-10">
              <Image
                src={srcStr}
                alt={alt || ""}
                style={{
                  objectFit: "cover",
                }}
                {...cleanHtmlRestProps(restProps)}
                width={width}
                height={height}
              />
            </div>
          );
        },
      }}
    >
      {content}
    </ReactMarkdown>
    </div>
  );
};
